import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { comunicadoSistemaQueries } from "@/services/api/queries/comunicado-sistema.queries";
import { NIVEL_CONFIG } from "@/components/comunicados/comunicado-nivel";
import { ComunicadoSistemaVigenteDTO } from "@/types/comunicado-sistema.type";

const DISPENSADOS_KEY = "comunicados-sistema-dispensados";

function lerDispensados(): number[] {
  try {
    const raw = localStorage.getItem(DISPENSADOS_KEY);
    return raw ? (JSON.parse(raw) as number[]) : [];
  } catch {
    return [];
  }
}

/** Ordena pela "fila": maior nível primeiro, depois início mais recente. */
function ordenar(a: ComunicadoSistemaVigenteDTO, b: ComunicadoSistemaVigenteDTO): number {
  const peso = NIVEL_CONFIG[b.nivel].peso - NIVEL_CONFIG[a.nivel].peso;
  if (peso !== 0) return peso;
  return new Date(b.inicioEm).getTime() - new Date(a.inicioEm).getTime();
}

/**
 * Fonte única dos comunicados vigentes para barra/sino/mural. Em erro devolve
 * lista vazia (nunca lança) — a comunicação não pode derrubar o Header.
 */
export function useComunicadosSistemaVigentes() {
  const { data = [] } = useQuery({
    ...comunicadoSistemaQueries.vigentes(),
    throwOnError: false,
  });

  const [dispensados, setDispensados] = useState<number[]>(lerDispensados);

  const vigentes = useMemo(() => [...data].sort(ordenar), [data]);

  const dispensar = useCallback((id: number) => {
    setDispensados((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      try {
        localStorage.setItem(DISPENSADOS_KEY, JSON.stringify(next));
      } catch {
        /* localStorage indisponível: dispensa só nesta sessão */
      }
      return next;
    });
  }, []);

  // A barra é a superfície principal: todo nível passa por ela. O único corte é
  // o que o usuário já dispensou no "x" (não vale para crítico, que não tem "x")
  // — dispensado some da barra, mas continua no sino e no mural.
  const paraBarra = useMemo(
    () => vigentes.filter((c) => !dispensados.includes(c.id)),
    [vigentes, dispensados],
  );

  return {
    vigentes, // sino e mural mostram tudo
    topo: paraBarra[0] ?? null,
    total: vigentes.length,
    dispensar,
  };
}
